import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import { type GraphQLConnection } from "@/lib/graphql-pagination";

// --- Types ---

export interface PromiseBreakdownSegment {
  name: string;
  color: string;
  value: number;
  count: number;
  totalAmount: number;
  fill: string;
}

// --- GraphQL ---

interface PromiseToPayNode {
  promiseId: number;
  statusCode: string | null;
  statusName: string | null;
  promisedAmount: number;
}

interface PromiseToPayListResponse {
  promisesToPay: {
    promiseToPayList: GraphQLConnection<PromiseToPayNode>;
  };
}

const PROMISE_BREAKDOWN_QUERY = `
  query PromiseBreakdownList($first: Int, $after: String, $filter: PromiseToPayListFilterInput) {
    promisesToPay {
      promiseToPayList(first: $first, after: $after, filter: $filter) {
        edges {
          node {
            promiseId
            statusCode
            statusName
            promisedAmount
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// --- Constants ---

const STATUS_COLORS: Record<string, string> = {
  Paid: "#6baed6",
  "Partially Paid": "#f5a623",
  Broken: "#e05c5c",
  Active: "#0d9488",
  Closed: "#9ca3af",
};

const DEFAULT_COLOR = "#d1d5db";

// --- Mock data ---

function generateMockBreakdown(): PromiseBreakdownSegment[] {
  const segments = [
    { name: "Paid", pct: 28, valuePerCase: 12400 },
    { name: "Partially Paid", pct: 18, valuePerCase: 7200 },
    { name: "Broken", pct: 24, valuePerCase: 18600 },
    { name: "Active", pct: 20, valuePerCase: 9800 },
    { name: "Closed", pct: 10, valuePerCase: 5300 },
  ];
  return segments.map(seg => {
    const count = Math.max(1, Math.round((seg.pct / 100) * 50));
    const totalAmount = Math.round(count * seg.valuePerCase);
    const color = STATUS_COLORS[seg.name] ?? DEFAULT_COLOR;
    return { name: seg.name, color, value: seg.pct, count, totalAmount, fill: color };
  });
}

// --- Hook ---

export function usePromiseBreakdownGraphQL(
  selectedUserId?: string,
  dateFrom?: Date,
  dateTo?: Date,
  currencyCode?: string,
) {
  const apiConfig = useApiConfig();
  const mockData = generateMockBreakdown();

  return useModeAwareQuery<PromiseBreakdownSegment[], Error>(
    ["promise-breakdown", selectedUserId, dateFrom?.toISOString(), dateTo?.toISOString(), currencyCode],
    async ({ signal }) => {
      const filter: Record<string, unknown> = {};

      if (selectedUserId && selectedUserId !== "everyone") {
        filter.assignedUserId = { eq: Number(selectedUserId) };
      }

      if (currencyCode) {
        filter.currencyCode = { eq: currencyCode };
      }

      if (dateFrom || dateTo) {
        const dateFilter: Record<string, string> = {};
        if (dateFrom) {
          const from = new Date(dateFrom);
          from.setHours(0, 0, 0, 0);
          dateFilter.ge = from.toISOString();
        }
        if (dateTo) {
          const to = new Date(dateTo);
          to.setHours(23, 59, 59, 999);
          dateFilter.le = to.toISOString();
        }
        filter.createdAt = dateFilter;
      }

      // Fetch all promises matching the filter (up to 500)
      let allNodes: PromiseToPayNode[] = [];
      let hasMore = true;
      let after: string | null = null;

      while (hasMore) {
        const variables: Record<string, unknown> = { first: 500, filter };
        if (after) variables.after = after;

        const response = await graphqlQuery<PromiseToPayListResponse>(
          apiConfig,
          PROMISE_BREAKDOWN_QUERY,
          variables,
          signal,
        );

        const connection = response.promisesToPay.promiseToPayList;
        allNodes = [...allNodes, ...connection.edges.map(x => x.node)];
        hasMore = connection.pageInfo.hasNextPage;
        after = connection.pageInfo.endCursor;

        // Safety: cap at 2000 records
        if (allNodes.length >= 2000) break;
      }

      // Group by status
      const groupMap = new Map<string, { count: number; totalAmount: number }>();
      for (const node of allNodes) {
        const status = node.statusName ?? node.statusCode ?? "Unknown";
        const existing = groupMap.get(status) ?? { count: 0, totalAmount: 0 };
        existing.count += 1;
        existing.totalAmount += node.promisedAmount;
        groupMap.set(status, existing);
      }

      const totalCount = allNodes.length;

      const segments: PromiseBreakdownSegment[] = [];
      for (const [name, data] of groupMap.entries()) {
        const color = STATUS_COLORS[name] ?? DEFAULT_COLOR;
        const value = totalCount > 0 ? Math.round((data.count / totalCount) * 100) : 0;
        segments.push({
          name,
          color,
          value,
          count: data.count,
          totalAmount: data.totalAmount,
          fill: color,
        });
      }

      // Sort by value descending
      segments.sort((a, b) => b.value - a.value);

      return segments;
    },
    mockData,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    },
  );
}
